// Chart layer shared by the team reports and the dashboards.
//
// Lives in its own module because both `ReportsTeamView` (one report, one
// chart) and `TeamDashboardsTab` (many widgets, one chart each) need it. Having
// the dashboard import it back out of ReportsTeamView would be a cycle.
//
// Rows here are PEOPLE, not points in time, which is why there is no line
// option anywhere below: a line implies an ordered, continuous x-axis, and a
// list of teammates is neither.

import { useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LabelList,
  PieChart as RPieChart, Pie, Cell, Legend,
} from 'recharts';
import { BarChart2, PieChart as PieIcon, Table as TableIcon } from 'lucide-react';

export const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Slice colors for the pie. Assigned in fixed order and never cycled — past
// the 8th member the rest fold into "Otros" (see pieData), because a 9th
// generated hue is indistinguishable from one already on screen.
export const SLICE_COLORS = ['#00d4ff', '#a78bfa', '#00ff88', '#ffb700', '#f472b6', '#60a5fa', '#fb923c', '#34d399'];
const OTHER_COLOR = '#5c7d9c';
const MAX_SLICES = 8;

export const fmtBy = (unit, v) =>
  unit === 'money' ? money(v)
  : unit === 'pct' ? `${Number(v || 0)}%`
  : Number(v || 0).toLocaleString('en-US');

// Compact axis ticks so long money values don't crowd the plot.
export const axisTick = (unit) => (v) => {
  if (unit === 'pct') return `${v}%`;
  if (unit === 'money') return Math.abs(v) >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`;
  return Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : String(v);
};

export function ReportChart({ rows, meta, chartType, chartKey, onChartType, onChartKey, loading, t, inp, preserveOrder = false }) {
  const chartable = (meta?.columns || []).filter((c) => c.raw);
  const col = chartable.find((c) => c.key === chartKey);
  if (loading || !rows.length || !chartable.length || !col) return null;

  // Sorted descending: the ranking IS the message of a per-person chart.
  //
  // Except when the rows are MONTHS. A timeline read as a ranking is not a
  // timeline: "marzo, agosto, enero" ordered by size hides the very thing a
  // month chart exists to show. Those callers pass preserveOrder and keep the
  // chronological order they built.
  const mapped = rows.map((r) => ({ name: r.name, value: col.raw(r) }));
  const data = preserveOrder ? mapped : mapped.sort((a, b) => b.value - a.value);

  // A pie only makes sense when the parts genuinely add to a whole, and only
  // for non-negative values. Averages and rates fail the first test.
  const total = data.reduce((s, d) => s + d.value, 0);
  const canPie = col.additive && data.every((d) => d.value >= 0) && total > 0;
  const type = chartType === 'pie' && !canPie ? 'bar' : chartType;

  // Past MAX_SLICES the tail folds into one "Otros" slice rather than getting
  // invented hues nobody can tell apart.
  const pieData = (() => {
    if (data.length <= MAX_SLICES) return data;
    const head = data.slice(0, MAX_SLICES - 1);
    const rest = data.slice(MAX_SLICES - 1).reduce((s, d) => s + d.value, 0);
    return [...head, { name: t('Otros', 'Other'), value: rest, _other: true }];
  })();

  // A widget passes null handlers; the reports tab passes real ones.
  const interactive = typeof onChartType === 'function' && typeof onChartKey === 'function';
  const tooltipStyle = { background: '#0d1c2e', border: '1px solid #1a3550', borderRadius: 8, fontSize: 12 };
  const barHeight = Math.max(180, data.length * 34 + 44);
  const tab = (on) => ({
    ...inp, padding: '.35rem .6rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
    fontWeight: 700, fontSize: '.75rem',
    color: on ? '#00d4ff' : 'var(--c-text3,#45688a)',
    borderColor: on ? '#00d4ff66' : 'var(--c-border,#1a3550)',
    background: on ? '#00d4ff18' : 'var(--c-surface2,#112538)',
  });

  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1rem', marginBottom: 12 }}>
      {/* Read-only inside a dashboard widget: the chart choice belongs to the
          saved widget, so offering a control here would look saved and wouldn't be. */}
      {interactive && (
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '.5rem', marginBottom: '.85rem' }}>
        <select value={chartKey} onChange={(e) => onChartKey(e.target.value)} style={{ ...inp, cursor: 'pointer', marginRight: 'auto' }}>
          {chartable.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button type="button" onClick={() => onChartType('bar')} style={tab(type === 'bar')}>
          <BarChart2 size={13} /> {t('Barras', 'Bars')}
        </button>
        <button
          type="button"
          onClick={() => onChartType('pie')}
          disabled={!canPie}
          title={canPie ? '' : t('Solo para columnas que se pueden sumar — un promedio o un porcentaje no reparten un total.', 'Only for columns that can be summed — an average or a percentage does not divide a whole.')}
          style={{ ...tab(type === 'pie'), cursor: canPie ? 'pointer' : 'not-allowed', opacity: canPie ? 1 : .45 }}
        >
          <PieIcon size={13} /> {t('Torta', 'Pie')}
        </button>
        <button type="button" onClick={() => onChartType('none')} style={tab(type === 'none')}>
          <TableIcon size={13} /> {t('Solo tabla', 'Table only')}
        </button>
      </div>
      )}

      {type === 'none' ? null : type === 'pie' ? (
        <ResponsiveContainer width="100%" height={300}>
          <RPieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} strokeWidth={2} stroke="#0d1c2e">
              {pieData.map((d, i) => (
                <Cell key={d.name} fill={d._other ? OTHER_COLOR : SLICE_COLORS[i % SLICE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(v, n) => [`${fmtBy(col.unit, v)} (${total ? Math.round((v / total) * 100) : 0}%)`, n]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </RPieChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={barHeight}>
          <BarChart data={data} layout="vertical" margin={{ left: 4, right: 56, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1a3550" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#7aa3c0' }} tickFormatter={axisTick(col.unit)} />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#7aa3c0' }} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmtBy(col.unit, v), col.label]} cursor={{ fill: '#ffffff08' }} />
            <Bar dataKey="value" fill={meta?.color || '#00d4ff'} radius={[0, 4, 4, 0]}>
              {/* The value is the point of the chart — label it directly rather
                  than making the reader trace back to the axis. */}
              <LabelList dataKey="value" position="right" formatter={(v) => fmtBy(col.unit, v)} style={{ fill: '#eef6ff', fontSize: 11, fontWeight: 700 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── Group-and-chart over a plain array of rows ───────────────────────────
// For the client-side reports (Reporte de Ventas, Reportes de Clientes) that
// already hold their rows in memory and filter them locally. Instead of a
// per-report chart, this takes the rows plus a list of DIMENSIONS to group by
// and MEASURES to add up, then hands the grouped result to ReportChart — so
// every chart in the app shares the same bar/pie rules, the same "Otros"
// folding, and the same refusal to draw a pie over an average.
//
// The default measure is always "how many rows", because a count is meaningful
// for every dimension; money measures are opt-in per report.
export function GroupedChart({ rows, dimensions, measures = [], lang, color = '#00d4ff' }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const [dimKey, setDimKey] = useState(dimensions[0]?.key || '');
  const [measKey, setMeasKey] = useState('__count__');
  const [chartType, setChartType] = useState('bar');

  const dim = dimensions.find((d) => d.key === dimKey) || dimensions[0];
  const meas = measures.find((m) => m.key === measKey);

  const grouped = useMemo(() => {
    if (!dim) return [];
    const acc = new Map();
    for (const row of rows || []) {
      // Blank is a real answer ("sin analista"), not a row to drop — hiding it
      // would make the chart add up to less than the table.
      const raw = String(dim.get(row) ?? '').trim();
      const label = raw || t('(sin dato)', '(blank)');
      const add = meas ? Number(meas.get(row) || 0) : 1;
      if (!Number.isFinite(add)) continue;
      acc.set(label, (acc.get(label) ?? 0) + add);
    }
    const out = [...acc.entries()].map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }));
    // Una dimensión de tiempo se ordena por fecha, no por tamaño. Las claves son
    // "YYYY-MM", así que ordenarlas como texto ya las deja en orden cronológico.
    return dim.timeline
      ? out.sort((a, b) => String(a.name).localeCompare(String(b.name)))
      : out.sort((a, b) => b.value - a.value);
  }, [rows, dim, meas, es]);

  // Synthesize the shape ReportChart expects, so the two stay in lockstep.
  const meta = useMemo(() => ({
    color,
    columns: [{
      key: 'value',
      label: meas ? meas.label : t('Cantidad', 'Count'),
      raw: (r) => Number(r.value || 0),
      unit: meas?.unit || 'num',
      // Counts and money sums genuinely divide a whole, so a pie is honest here.
      additive: true,
    }],
  }), [meas, color, es]);

  const inp = { padding: '.35rem .6rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.78rem', outline: 'none', fontFamily: 'inherit' };

  if (!dim || !rows || rows.length === 0) return null;

  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.9rem', marginBottom: '.9rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '.5rem', marginBottom: '.7rem' }}>
        <span style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {t('Agrupar por', 'Group by')}
        </span>
        <select value={dimKey} onChange={(e) => setDimKey(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
          {dimensions.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </select>
        {measures.length > 0 && (
          <select value={measKey} onChange={(e) => setMeasKey(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
            <option value="__count__">{t('Cantidad', 'Count')}</option>
            {measures.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        )}
      </div>
      <ReportChart
        rows={grouped}
        meta={meta}
        chartType={chartType}
        chartKey="value"
        onChartType={setChartType}
        onChartKey={() => {}}
        loading={false}
        t={t}
        inp={inp}
        preserveOrder={!!dim.timeline}
      />
    </div>
  );
}
